import React from "react";
import { WeatherForecast, WeatherType } from "../types/weatherTypes.js";

interface WeatherCardProps {
  forecast: WeatherForecast;
}

const WeatherCard: React.FC<WeatherCardProps> = ({ forecast }) => {
  const WeatherIcons = {
    [WeatherType.RAIN]: "cloud-rain",
    [WeatherType.STORM]: "bolt",
    [WeatherType.SNOW]: "snowflake",
    [WeatherType.SLEET]: "cloud-meatball",
    [WeatherType.FOG]: "smog",
    [WeatherType.WIND]: "wind",
    [WeatherType.HAIL]: "cloud-showers-heavy",
    [WeatherType.HEATWAVE]: "temperature-high",
    [WeatherType.FREEZING_RAIN]: "icicles",
  };

  const WeatherColors = {
    [WeatherType.RAIN]: "bg-blue-100",
    [WeatherType.STORM]: "bg-purple-100",
    [WeatherType.SNOW]: "bg-blue-50",
    [WeatherType.SLEET]: "bg-gray-100",
    [WeatherType.FOG]: "bg-gray-200",
    [WeatherType.WIND]: "bg-gray-100",
    [WeatherType.HAIL]: "bg-gray-300",
    [WeatherType.HEATWAVE]: "bg-red-100",
    [WeatherType.FREEZING_RAIN]: "bg-indigo-100",
  };

  // Calculate the animation speed based on forecast.precipitation
  const maxSpeed = 150;
  const minSpeed = 400;
  const speedRange = minSpeed - maxSpeed;

  // TODO: Make minimum and maximum constants
  const minPrecipitation = 30;
  const maxPrecipitation = 80;

  const ratio =
    1 -
    (forecast.precipitation - minPrecipitation) /
      (maxPrecipitation - minPrecipitation); // 0 when max precipitation; 1 when min precipitation

  const calculateSpeed = (forecastPrecipitation: number): number =>
    forecastPrecipitation < minPrecipitation
      ? minSpeed
      : forecastPrecipitation > maxPrecipitation
        ? maxSpeed
        : maxSpeed + speedRange * ratio;

  const animationSpeed = calculateSpeed(forecast.precipitation); // Speed in milliseconds

  return (
    <div
      className={`weather-card rounded-xl p-4 transition-all duration-300 ${WeatherColors[forecast.weatherType]} relative overflow-hidden border border-gray-300 text-black`}
    >
      {forecast.weatherType === WeatherType.HEATWAVE ? (
        <div
          className="fire-animation absolute inset-0 opacity-20"
          style={
            {
              "--animation-speed": `${animationSpeed}ms`,
            } as React.CSSProperties
          }
        />
      ) : (
        <div
          className="rain-animation absolute inset-0 opacity-20"
          style={
            {
              "--animation-speed": `${animationSpeed}ms`,
            } as React.CSSProperties
          }
        />
      )}

      <div className="relative z-10">
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-lg font-bold">{forecast.date}</h3>
          <div className="text-3xl">
            <i className={`fas fa-${WeatherIcons[forecast.weatherType]}`}></i>
          </div>
        </div>
        <div className="mb-2 text-4xl font-bold">{forecast.temp}°C</div>
        <div className="mb-1 text-sm text-gray-700">{forecast.description}</div>
        <div className="mb-1 text-xs text-gray-600">
          Feels like: {forecast.feelsLike}°C
        </div>
        {forecast.precipitation > 0 && (
          <div className="mb-1 text-xs text-gray-600">
            Precipitation: {forecast.precipitation}mm
          </div>
        )}
        <div className="mb-1 text-xs text-gray-600">
          Humidity: {forecast.humidity}%
        </div>
        <div className="text-xs text-gray-600">
          Wind: {forecast.windSpeed} km/h
        </div>
      </div>
    </div>
  );
};

export default WeatherCard;
